import React, { useState, useEffect } from 'react';
import { Save, Loader2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { formatTelefone, formatCNPJ } from '@/lib/utils';

const CreateEditModal = ({ isOpen, onClose, onSave, item, type, statusTypes = [], clients = [] }) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [responsaveis, setResponsaveis] = useState([]);
  const { toast } = useToast();

  // Carrega responsáveis da empresa ao abrir modal de meta, serviço ou implementação
  useEffect(() => {
    if ((type === 'services' || type === 'implementations' || type === 'goals' || type === 'attendance') && isOpen) {
      const fetchResponsaveis = async () => {
        try {
          const token = localStorage.getItem('auth_token');
          const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000/api'}/servicos/responsaveis`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
            },
          });
          if (!response.ok) throw new Error('Erro ao buscar responsáveis');
          const data = await response.json();
          setResponsaveis(data);
        } catch (error) {
          setResponsaveis([]);
        }
      };
      fetchResponsaveis();
    }
  }, [type, isOpen]);

  // Initialize form data with defaults or item data
  useEffect(() => {
    if (isOpen && !item) {
      // Defaults based on type
      const today = new Date().toISOString().split('T')[0];
      
      // Get default status from statusTypes or fallback
      const getDefaultStatus = () => {
        const filteredTypes = statusTypes.filter(st => 
          st.type === (type === 'goals' ? 'meta' : 
                      type === 'services' ? 'servico' : 
                      type === 'attendance' ? 'atendimento' : 
                      type === 'implementations' ? 'implementacao' : st.type)
        );
        return filteredTypes.length > 0 ? filteredTypes[0].name : 'Aberto';
      };
      
      const defaults = {
        openingDate: today,
        dueDate: today,
        lifecycleStatus: getDefaultStatus(),
        description: '',
        assignedTo: ''
      };
      
      switch(type) {
        case 'empresa':
          setFormData({ nome: '', cnpj: '', email: '', telefone: '' });
          break;
        case 'goals': 
          setFormData({ ...defaults, status: 'on-track', progress: 0, current: 0, target: 100 }); 
          break;
        case 'services': 
          setFormData({ ...defaults, status: 'on-track', client: '' }); 
          break;
        case 'attendance': 
          setFormData({ ...defaults, status: 'open', priority: 'media' }); 
          break;
        case 'implementations': 
          setFormData({ ...defaults, status: 'planning', progress: 0, checklist: [], client: '' }); 
          break;
        case 'statusType':
          setFormData({ name: '', type: 'meta' });
          break;
        default: 
          setFormData(defaults);
      }
    }
  }, [item, type, isOpen, statusTypes]);

  // Map item fields to form fields when editing
  useEffect(() => {
    if (item && type) {
      let mappedData = { ...item };

      if (type === 'empresa') {
        if (mappedData.telefone) mappedData.telefone = formatTelefone(mappedData.telefone);
        if (mappedData.cnpj) mappedData.cnpj = formatCNPJ(mappedData.cnpj);
      }

      if (type === 'services') {
        mappedData.name = item.nome || item.name || '';
        mappedData.description = item.descricao || item.description || '';
        mappedData.assignedTo = item.responsavel || item.assignedTo || '';
        mappedData.client = (item.cliente && item.cliente.id) ? item.cliente.id : (item.cliente_id || item.client || '');
        mappedData.openingDate = item.data_abertura || item.openingDate || '';
        mappedData.dueDate = item.prazo || item.dueDate || '';
        mappedData.lifecycleStatus = item.status || item.lifecycleStatus || 'Aberto';
        mappedData.progresso_atual = item.progresso_atual ?? 0;
        mappedData.progresso_objetivo = item.progresso_objetivo ?? 1;
      }

      if (type === 'goals') {
        mappedData.name = item.titulo || item.name || '';
        mappedData.description = item.descricao || item.description || '';
        mappedData.assignedTo = item.responsavel || item.assignedTo || '';
        mappedData.openingDate = item.data_inicio || item.openingDate || '';
        mappedData.dueDate = item.data_fim || item.dueDate || '';
        mappedData.lifecycleStatus = item.status || item.lifecycleStatus || 'Aberto';
        mappedData.current = item.progresso_atual ?? item.current ?? 0;
        mappedData.target = item.progresso_objetivo ?? item.target ?? 100;
      }

      if (type === 'implementations') {
        mappedData.name = item.titulo || item.nome || item.name || '';
        mappedData.description = item.descricao || item.description || '';
        mappedData.assignedTo = item.responsavel || item.assignedTo || '';
        mappedData.client = item.cliente || item.client || '';
        mappedData.openingDate = item.data_abertura || item.openingDate || '';
        mappedData.dueDate = item.prazo || item.dueDate || '';
        mappedData.lifecycleStatus = item.status || item.lifecycleStatus || 'Aberto';
        mappedData.progress = item.progresso ?? item.progress ?? 0;
      }

      if (type === 'attendance') {
        mappedData.title = item.titulo || item.title || '';
        mappedData.description = item.descricao || item.description || '';
        mappedData.assignedTo = item.responsavel || item.assignedTo || '';
        mappedData.openingDate = item.data_abertura || item.openingDate || '';
        mappedData.dueDate = item.prazo || item.dueDate || '';
        mappedData.lifecycleStatus = item.status || item.lifecycleStatus || 'Aberto';
        mappedData.priority = (item.prioridade || item.priority || 'media') === 'medium' ? 'media' : (item.prioridade || item.priority || 'media');
      }

      setFormData(mappedData);
    }
  }, [item, type]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    
    if (type === 'empresa') {
      if (name === 'telefone') {
        formattedValue = formatTelefone(value);
      } else if (name === 'cnpj') {
        formattedValue = formatCNPJ(value);
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: formattedValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (type === 'empresa') {
        if (!formData.nome) throw new Error('Nome da empresa é obrigatório');
      } else if (type === 'statusType') {
        if (!formData.name) throw new Error('Nome é obrigatório');
        if (!formData.type) throw new Error('Tipo é obrigatório');
      } else {
        if (!formData.name && !formData.title) throw new Error("Nome/Título é obrigatório");
        if (!formData.openingDate) throw new Error("Data de abertura é obrigatória");
        if (!formData.dueDate) throw new Error("Prazo é obrigatório");
      }
      
      let finalData = { ...formData };
      if (finalData.lifecycleStatus === 'Concluído' && !finalData.completedAt) {
        finalData.completedAt = new Date().toISOString();
      } else if (finalData.lifecycleStatus === 'Aberto') {
        finalData.completedAt = null;
      }

      await onSave(finalData);
      // Don't close here, let parent close
    } catch (error) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Comparison view helper
  const renderSideBySide = (label, currentValue, originalValue) => {
    if (!item) return null; // Only for edit mode
    if (currentValue === originalValue) return null;
    
    return (
      <div className="text-xs text-orange-400 mt-1 flex items-center gap-1">
        <span className="opacity-50 font-mono">Original:</span>
        <span className="font-mono bg-white/5 px-1 rounded">{originalValue || '-'}</span>
      </div>
    );
  };

  const renderFields = () => {
    return (
      <div className="grid gap-6 py-6">
        {/* Comparison Header if Editing */}
        {item && (
          <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-sm text-orange-300 mb-2">
            <span className="font-bold">Modo de Edição:</span> Alterações serão destacadas abaixo.
          </div>
        )}

        {/* Common Fields */}
        <div className="grid grid-cols-1 gap-2">
          <label className="text-sm font-bold text-[#FFD700] uppercase tracking-wide">
            {type === 'attendance' ? 'Título' : (type === 'empresa' ? 'Nome da Empresa' : 'Nome')} <span className="text-red-500">*</span>
          </label>
          <input
            name={type === 'attendance' ? 'title' : (type === 'empresa' ? 'nome' : 'name')}
            value={formData[type === 'attendance' ? 'title' : (type === 'empresa' ? 'nome' : 'name')] || ''}
            onChange={handleChange}
            className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-[#FF8C00] focus:ring-0 transition-colors font-medium text-lg"
            placeholder={`Digite o ${type === 'attendance' ? 'título' : (type === 'empresa' ? 'nome da empresa' : 'nome')}...`}
            required
          />
          {renderSideBySide('Nome', formData[type === 'attendance' ? 'title' : (type === 'empresa' ? 'nome' : 'name')], item?.[type === 'attendance' ? 'title' : (type === 'empresa' ? 'nome' : 'name')])}
        </div>

        {type !== 'empresa' && (
          <div className="grid grid-cols-1 gap-2">
            <label className="text-sm font-bold text-[#FFD700] uppercase tracking-wide">
              Descrição
            </label>
            <textarea
              name="description"
              value={formData.description || ''}
              onChange={handleChange}
              className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-[#FF8C00] focus:ring-0 transition-colors h-24 resize-none"
              placeholder="Adicione detalhes..."
            />
            {renderSideBySide('Descrição', formData.description, item?.description)}
          </div>
        )}

        {/* Type Specific Fields */}
        {type === 'goals' && (
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-bold text-[#FFD700] uppercase tracking-wide block mb-2">Valor Atual</label>
              <input
                type="number"
                name="current"
                value={formData.current || 0}
                onChange={handleChange}
                className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-[#0066FF] font-bold focus:border-[#0066FF]"
              />
            </div>
            <div>
              <label className="text-sm font-bold text-[#FFD700] uppercase tracking-wide block mb-2">Meta Alvo</label>
              <input
                type="number"
                name="target"
                value={formData.target || 0}
                onChange={handleChange}
                className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-[#0066FF] font-bold focus:border-[#0066FF]"
              />
            </div>
          </div>
        )}

        {type === 'services' && (
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-bold text-[#FFD700] uppercase tracking-wide block mb-2">Progresso Atual</label>
              <input
                type="number"
                name="progresso_atual"
                value={formData.progresso_atual || 0}
                onChange={handleChange}
                className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-[#0066FF] font-bold focus:border-[#0066FF]"
              />
            </div>
            <div>
              <label className="text-sm font-bold text-[#FFD700] uppercase tracking-wide block mb-2">Progresso Objetivo</label>
              <input
                type="number"
                name="progresso_objetivo"
                value={formData.progresso_objetivo || 1}
                onChange={handleChange}
                className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-[#0066FF] font-bold focus:border-[#0066FF]"
              />
            </div>
          </div>
        )}

        {(type === 'services' || type === 'implementations') && (
          <div className="grid grid-cols-1 gap-2">
            <label className="text-sm font-bold text-[#FFD700] uppercase tracking-wide">Cliente</label>
            <select
              name="client"
              value={formData.client || ''}
              onChange={handleChange}
              className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#FF8C00]"
            >
              <option value="">Selecione um cliente</option>
              {clients.map((c) => (
                <option key={c.id} value={type === 'implementations' ? c.nome : c.id}>{c.nome} {c.email ? `(${c.email})` : ''}</option>
              ))}
            </select>
            {renderSideBySide('Cliente', formData.client, item?.client)}
          </div>
        )}

        {type === 'empresa' && (
          <div className="grid grid-cols-1 gap-2">
            <label className="text-sm font-bold text-[#FFD700] uppercase tracking-wide">CNPJ</label>
            <input
              name="cnpj"
              value={formData.cnpj || ''}
              onChange={handleChange}
              className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#FF8C00]"
              placeholder="00.000.000/0000-00"
            />

            <label className="text-sm font-bold text-[#FFD700] uppercase tracking-wide">Email</label>
            <input
              name="email"
              type="email"
              value={formData.email || ''}
              onChange={handleChange}
              className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#FF8C00]"
              placeholder="contato@empresa.com"
            />

            <label className="text-sm font-bold text-[#FFD700] uppercase tracking-wide">Telefone</label>
            <input
              name="telefone"
              value={formData.telefone || ''}
              onChange={handleChange}
              className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#FF8C00]"
              placeholder="11 99999-9999"
            />
          </div>
        )}

        {type === 'statusType' && (
          <div className="grid grid-cols-1 gap-2">
            <label className="text-sm font-bold text-[#FFD700] uppercase tracking-wide">Tipo</label>
            <select
              name="type"
              value={formData.type || 'meta'}
              onChange={handleChange}
              className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#FF8C00]"
            >
              <option value="meta">Meta</option>
              <option value="atendimento">Atendimento</option>
              <option value="servico">Serviço</option>
              <option value="implementacao">Implementação</option>
            </select>
          </div>
        )}

        {type === 'attendance' && (
          <div className="grid grid-cols-1 gap-2">
             <label className="text-sm font-bold text-[#FFD700] uppercase tracking-wide block mb-2">Prioridade</label>
              <select
                name="priority"
                value={formData.priority || 'media'}
                onChange={handleChange}
                className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#FF8C00]"
              >
                <option value="baixa">Baixa</option>
                <option value="media">Média</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
          </div>
        )}

        {type === 'implementations' && (
            <div className="grid grid-cols-1 gap-2">
              <label className="text-sm font-bold text-[#FFD700] uppercase tracking-wide">Progresso (%)</label>
               <input
                type="number"
                name="progress"
                min="0"
                max="100"
                value={formData.progress || 0}
                onChange={handleChange}
                className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-[#0066FF] font-bold focus:border-[#0066FF]"
              />
            </div>
        )}

        {type !== 'empresa' && (
          <>
            {/* Common Dates & Status */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-bold text-[#FFD700] uppercase tracking-wide block mb-2">Data Abertura <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  name="openingDate"
                  value={formData.openingDate || ''}
                  onChange={handleChange}
                  className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-[#FF8C00] font-medium focus:border-[#FF8C00]"
                  required
                />
                {renderSideBySide('Abertura', formData.openingDate, item?.openingDate)}
              </div>
              <div>
                <label className="text-sm font-bold text-[#FFD700] uppercase tracking-wide block mb-2">Prazo <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate || ''}
                  onChange={handleChange}
                  className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-[#FF8C00] font-medium focus:border-[#FF8C00]"
                  required
                />
                {renderSideBySide('Prazo', formData.dueDate, item?.dueDate)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-bold text-[#FFD700] uppercase tracking-wide block mb-2">Responsável</label>
                {(type === 'services' || type === 'implementations' || type === 'goals' || type === 'attendance') ? (
                  <select
                    name="assignedTo"
                    value={formData.assignedTo || ''}
                    onChange={handleChange}
                    className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#FF8C00]"
                  >
                    <option value="">Nenhum</option>
                    {responsaveis.map((r) => (
                      <option key={r.id} value={r.name}>{r.name} ({r.email}) - {r.role}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    name="assignedTo"
                    value={formData.assignedTo || ''}
                    onChange={handleChange}
                    className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#FF8C00]"
                  />
                )}
                {renderSideBySide('Responsável', formData.assignedTo, item?.assignedTo)}
              </div>
              <div>
                <label className="text-sm font-bold text-[#FFD700] uppercase tracking-wide block mb-2">Status</label>
                <select
                  name="lifecycleStatus"
                  value={formData.lifecycleStatus || 'Aberto'}
                  onChange={handleChange}
                  className="w-full bg-slate-800 border-2 border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#FF8C00]"
                >
                  {(() => {
                    const filteredTypes = statusTypes.filter(st => 
                      st.type === (type === 'goals' ? 'meta' : 
                                  type === 'services' ? 'servico' : 
                                  type === 'attendance' ? 'atendimento' : 
                                  type === 'implementations' ? 'implementacao' : st.type)
                    );
                    
                    return (
                      <>
                        {filteredTypes.map(st => (
                          <option key={st.id} value={st.name}>{st.name}</option>
                        ))}
                        <option value="Aberto">Aberto</option>
                        <option value="Concluído">Concluído</option>
                      </>
                    );
                  })()}
                </select>
                {renderSideBySide('Status', formData.lifecycleStatus, item?.lifecycleStatus)}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const getTitle = () => {
    const action = item ? 'Editar' : 'Criar';
    switch(type) {
      case 'goals': return `${action} Meta`;
      case 'services': return `${action} Serviço`;
      case 'attendance': return `${action} Atendimento`;
      case 'implementations': return `${action} Implementação`;
      default: return `${action} Item`;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700 text-white shadow-2xl">
        <DialogHeader className="border-b border-slate-800 pb-4">
          <DialogTitle className="text-2xl font-bold text-[#FF8C00]">{getTitle()}</DialogTitle>
          <DialogDescription className="text-slate-400">
            {item ? 'Edite os campos abaixo e salve as alterações.' : 'Preencha os campos abaixo para criar um novo item.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          {renderFields()}
          <DialogFooter className="border-t border-slate-800 pt-4 gap-2">
            <Button type="button" variant="ghost" onClick={onClose} className="hover:bg-slate-800 text-slate-300">
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="bg-[#FF8C00] hover:bg-orange-600 text-white gap-2 font-bold px-6"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {item ? 'Salvar Alterações' : 'Criar Item'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateEditModal;